import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { BaseCellComponent } from '../base-cell.component';
import { toContextArray } from 'src/app/common/bhub-common-module/utils/context-array.util';
import { ViewManager, ViewTab, SaveOperation, SaveContextFieldMapping } from 'src/app/board/pages/admin/admin-components/custom-views-config/view-builder/models/view-builder.models';
import { DynamicTableService } from 'src/app/services/dynamic-table.service';
import { DynamicTableEntriesResponse } from 'src/app/services/models/dynamic-table.models';
import { Subject, takeUntil, Observable, forkJoin, of } from 'rxjs';

interface OperationResult {
  operationId: string;
  success: boolean;
  responseData: any;
  error?: any;
}

@Component({
  selector: 'bhub-action-cell',
  standalone: false,
  templateUrl: './action-cell.component.html',
  styleUrl: './action-cell.component.scss'
})
export class ActionCellComponent extends BaseCellComponent implements OnInit, OnDestroy {
  @Input() availableTabs: ViewTab[] = [];
  @Input() viewManager: ViewManager | null = null;
  @Output() tabNavigation = new EventEmitter<string>();
  @Output() contextDataChange = new EventEmitter<{[contextKey: string]: any}>();
  @Output() refreshCells = new EventEmitter<string[]>();

  // UI Properties
  buttonText: string = 'Action';
  buttonColor: string = 'primary';
  buttonStyle: string = 'raised';
  actionType: 'navigate' | 'save' | 'clear_context' = 'navigate';

  // Configuration properties
  navigationTabId: string = '';
  contextObjectKey: string = '';
  datasetId: number | null = null;
  saveMode: 'update' | 'add' | 'auto' | 'delete' | 'upsert' = 'update';
  saveContextFieldMappings: SaveContextFieldMapping[] = [];
  saveOperations: SaveOperation[] = [];
  autoTrimStrings: boolean = true;
  private postSaveRefreshCellIds: string[] = [];
  private clearContextVariables: string[] = [];

  // State for chained saves
  private operationResults: Map<string, OperationResult> = new Map();

  // State
  private destroy$ = new Subject<void>();
  isProcessing = false;

  constructor(private dynamicTableService: DynamicTableService) {
    super();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected initializeCell(): void {
    this.updateActionConfiguration();
  }

  protected onCellOrDataChange(): void {
    this.updateActionConfiguration();
  }

  protected onCellUpdate(): void {
    this.updateActionConfiguration();
  }

  protected onDataSet(data: any): void {
    // Action buttons don't typically need to respond to external data changes
  }

  private updateActionConfiguration(): void {
    if (!this.cell) return;

    // Get basic configuration
    this.buttonText = this.getConfigValue('buttonText', 'Action');
    this.buttonColor = this.getConfigValue('buttonColor', 'primary');
    this.buttonStyle = this.getConfigValue('buttonStyle', 'raised');
    this.actionType = this.getConfigValue('actionType', 'navigate') as 'navigate' | 'save' | 'clear_context';

    // Navigation configuration
    this.navigationTabId = this.getConfigValue('navigationTabId', '');

    // Save data configuration
    this.contextObjectKey = this.getDataBindingValue('dataObjectKey') || '';
    this.datasetId = this.getDataBindingValue('datasetId') || null;
    this.saveMode = this.getConfigValue('saveMode', 'update') as 'update' | 'add' | 'auto' | 'delete' | 'upsert';
    this.saveContextFieldMappings = this.getConfigValue('saveContextFieldMappings', []) as SaveContextFieldMapping[];
    this.saveOperations = this.getConfigValue('saveOperations', []) as SaveOperation[];
    this.autoTrimStrings = this.getConfigValue('autoTrimStrings', true) as boolean;
    this.postSaveRefreshCellIds = this.getConfigValue('postSaveRefreshCellIds', []) as string[];
    this.clearContextVariables = this.getConfigValue('clearContextVariables', []) as string[];
  }

  onButtonClick(): void {
    if (this.isProcessing) return;

    if (this.actionType === 'navigate') {
      this.handleNavigation();
    } else if (this.actionType === 'save') {
      this.handleSaveData();
    } else if (this.actionType === 'clear_context') {
      this.handleClearContext();
    }
  }

  private handleNavigation(): void {
    if (this.navigationTabId) {
      this.tabNavigation.emit(this.navigationTabId);
    }
  }

  private handleClearContext(): void {
    if (!this.clearContextVariables || this.clearContextVariables.length === 0) {
      this.showMessage('No context objects configured to clear', 'error');
      return;
    }

    if (!this.viewManager?.contextData) {
      this.showMessage('No context data available', 'error');
      return;
    }

    const contextUpdates: { [contextKey: string]: any } = {};

    for (const contextKey of this.clearContextVariables) {
      const contextObject = this.viewManager.contextData[contextKey];
      if (!contextObject || typeof contextObject !== 'object') continue;

      if (Array.isArray(contextObject)) {
        // For arrays, clear to empty array
        this.viewManager.contextData[contextKey] = [];
        contextUpdates[contextKey] = [];
      } else {
        // For objects, null out all field values and mark as new
        const cleared: any = { __isNew: true, __metadata: { isNewRecord: true, isEmptyContext: true } };
        for (const key of Object.keys(contextObject)) {
          if (key === '__metadata' || key === '__isNew') continue;
          cleared[key] = null;
        }
        this.viewManager.contextData[contextKey] = cleared;
        contextUpdates[contextKey] = cleared;
      }
    }

    this.contextDataChange.emit(contextUpdates);

    // Trigger refresh on configured cells
    this.emitPostSaveRefresh();
  }

  private handleSaveData(): void {
    // Check if we have save operations configured (chained saves)
    if (this.saveOperations && this.saveOperations.length > 0) {
      this.executeChainedSaveOperations();
    } else {
      // Fall back to legacy single-context save
      this.saveContextDataToDataset();
    }
  }

  private saveContextDataToDataset(): void {
    // Validate required configuration
    if (!this.datasetId) {
      this.showMessage('No dataset selected for saving', 'error');
      return;
    }

    if (!this.contextObjectKey || !this.viewManager?.contextData) {
      this.showMessage('No context object selected for saving', 'error');
      return;
    }

    const rawContextObject = this.viewManager.contextData[this.contextObjectKey];
    if (!rawContextObject) {
      this.showMessage('Selected context object not found', 'error');
      return;
    }
    // Normalize list contexts to a flat array of rows so the save path never sees
    // a container object where it expects rows.
    const contextObject = this.unwrapListContext(rawContextObject);

    // Apply context field mappings — pull values from other context objects into this one before saving
    if (this.saveContextFieldMappings && this.saveContextFieldMappings.length > 0) {
      this.applyContextFieldMappings(contextObject);
    }

    // Handle arrays with auto mode: split new vs existing rows
    if (Array.isArray(contextObject) && this.saveMode === 'auto') {
      this.saveBulkAutoMode(contextObject);
      return;
    }

    // Determine if this is a new record (for auto mode)
    const isNewRecord = this.detectIsNewRecord(contextObject);

    // Determine which operation to perform based on saveMode
    const shouldAdd = this.saveMode === 'add' || (this.saveMode === 'auto' && isNewRecord);
    const shouldDelete = this.saveMode === 'delete';
    const shouldUpsert = this.saveMode === 'upsert';

    // Extract data from context object, excluding metadata fields
    let dataToSave;
    if (Array.isArray(contextObject)) {
      dataToSave = contextObject.map(item => {
        if (typeof item === 'object' && item !== null) {
          return this.cleanDataForSave(item);
        }
        return item;
      });
    } else if (typeof contextObject === 'object' && contextObject !== null) {
      dataToSave = [this.cleanDataForSave(contextObject)];
    } else {
      this.showMessage('Context object must be an object or array', 'error');
      return;
    }

    if (!dataToSave || dataToSave.length === 0) {
      this.showMessage('No data found in context object', 'error');
      return;
    }

    // Auto-trim string fields (no-op when feature disabled)
    dataToSave = this.applyAutoTrim(dataToSave);

    this.isProcessing = true;

    let saveOperation;
    let actionVerb: string;
    if (shouldDelete) {
      saveOperation = this.dynamicTableService.deleteEntries(this.datasetId, dataToSave);
      actionVerb = 'deleted';
    } else if (shouldUpsert) {
      saveOperation = this.dynamicTableService.upsertEntries(this.datasetId, dataToSave);
      actionVerb = 'saved';
    } else if (shouldAdd) {
      saveOperation = this.dynamicTableService.addEntries(this.datasetId, dataToSave);
      actionVerb = 'created';
    } else {
      saveOperation = this.dynamicTableService.updateEntries(this.datasetId, dataToSave);
      actionVerb = 'updated';
    }

    saveOperation
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (success) => {
          this.isProcessing = false;
          if (success) {
            this.showMessage(`Data ${actionVerb} successfully!`, 'success');

            // After insert or upsert, strip __isNew so subsequent saves don't re-insert.
            if (shouldAdd || shouldUpsert) {
              this.markContextAsExisting(contextObject);
            }

            this.emitPostSaveRefresh();
          } else {
            this.showMessage(`Error performing ${actionVerb} operation`, 'error');
          }
        },
        error: (error) => {
          this.isProcessing = false;
          const actionVerb = shouldAdd ? 'creating' : 'updating';
          console.error(`Error ${actionVerb} context data to dataset:`, error);
          this.showMessage(`Error ${actionVerb} data`, 'error');
        }
      });
  }

  /**
   * Handle bulk save for arrays in auto mode.
   * Splits rows by __isNew flag: new rows go to addEntries, existing rows go to updateEntries.
   * Both operations run in parallel. After success, __isNew flags are stripped.
   */
  private saveBulkAutoMode(contextArray: any[]): void {
    const newRows: any[] = [];
    const existingRows: any[] = [];

    contextArray.forEach(item => {
      if (typeof item === 'object' && item !== null) {
        if (this.detectIsNewRecord(item)) {
          newRows.push(item);
        } else {
          existingRows.push(item);
        }
      }
    });

    // Check for deleted rows stored by the data entry table
    const deletedRows: any[] = [];
    const deletedKey = this.contextObjectKey + '__deleted';
    if (this.viewManager?.contextData?.[deletedKey]) {
      const deleted = this.viewManager.contextData[deletedKey];
      if (Array.isArray(deleted)) {
        deleted.forEach(item => {
          if (typeof item === 'object' && item !== null) {
            deletedRows.push(this.cleanDataForSave(item));
          }
        });
      }
    }

    const cleanNewRows = this.applyAutoTrim(newRows.map(r => this.cleanDataForSave(r)));
    const cleanExistingRows = this.applyAutoTrim(existingRows.map(r => this.cleanDataForSave(r)));
    const trimmedDeletedRows = this.applyAutoTrim(deletedRows);

    if (cleanNewRows.length === 0 && cleanExistingRows.length === 0 && trimmedDeletedRows.length === 0) {
      this.showMessage('No changes to save', 'error');
      return;
    }

    this.isProcessing = true;

    // Build parallel operations (skip empty batches)
    const operations: Observable<boolean>[] = [];
    if (cleanNewRows.length > 0) {
      operations.push(this.dynamicTableService.addEntries(this.datasetId!, cleanNewRows));
    }
    if (cleanExistingRows.length > 0) {
      operations.push(this.dynamicTableService.updateEntries(this.datasetId!, cleanExistingRows));
    }
    if (trimmedDeletedRows.length > 0) {
      operations.push(this.dynamicTableService.deleteEntries(this.datasetId!, trimmedDeletedRows));
    }

    forkJoin(operations)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (results) => {
          this.isProcessing = false;
          const allSuccess = results.every(r => r);

          if (allSuccess) {
            const parts: string[] = [];
            if (cleanNewRows.length > 0) parts.push(`${cleanNewRows.length} added`);
            if (cleanExistingRows.length > 0) parts.push(`${cleanExistingRows.length} updated`);
            if (deletedRows.length > 0) parts.push(`${deletedRows.length} deleted`);
            this.showMessage(`Data saved successfully! (${parts.join(', ')})`, 'success');

            // Strip __isNew flags so subsequent saves treat all rows as updates
            this.markContextAsExisting(contextArray);

            // Clear the deleted rows tracker
            if (this.viewManager?.contextData?.[deletedKey]) {
              this.viewManager.contextData[deletedKey] = [];
            }

            this.emitPostSaveRefresh();
          } else {
            this.showMessage('Some operations failed while saving data', 'error');
          }
        },
        error: (error) => {
          this.isProcessing = false;
          console.error('Error during bulk save:', error);
          this.showMessage('Error saving data', 'error');
        }
      });
  }

  /**
   * Detects if the context object represents a new record that hasn't been saved yet.
   * Checks for __isNew flag, __metadata.isNewRecord flag, or __metadata.isEmptyContext flag.
   */
  private detectIsNewRecord(contextObject: any): boolean {
    if (!contextObject || typeof contextObject !== 'object') {
      return false;
    }

    // Check direct __isNew flag
    if (contextObject.__isNew === true) {
      return true;
    }

    // Check __metadata.isNewRecord flag
    if (contextObject.__metadata && contextObject.__metadata.isNewRecord === true) {
      return true;
    }

    // Check __metadata.isEmptyContext flag (set when created via filter list "create new" navigation)
    if (contextObject.__metadata && contextObject.__metadata.isEmptyContext === true) {
      return true;
    }

    return false;
  }

  /**
   * Removes metadata fields from the data before saving.
   * This includes __metadata, __isNew, and any other internal fields.
   */
  private cleanDataForSave(data: any): any {
    if (!data || typeof data !== 'object') {
      return data;
    }

    const { __metadata, __isNew, ...cleanData } = data;
    return cleanData;
  }

  /**
   * Strip leading/trailing whitespace from string values when autoTrimStrings is enabled.
   * Mutates each row in place — safe because rows passed here are already fresh copies
   * produced by cleanDataForSave (so the shared context object is not touched).
   */
  private applyAutoTrim(rows: any[]): any[] {
    if (!this.autoTrimStrings) return rows;
    for (const row of rows) {
      if (!row || typeof row !== 'object') continue;
      for (const key of Object.keys(row)) {
        const v = row[key];
        if (typeof v === 'string') {
          row[key] = v.trim();
        }
      }
    }
    return rows;
  }

  /**
   * After successfully adding a new record, update the context object
   * to mark it as existing so subsequent saves become updates.
   */
  private markContextAsExisting(contextObject: any): void {
    if (!this.viewManager?.contextData || !this.contextObjectKey) {
      return;
    }

    const currentContext = this.viewManager.contextData[this.contextObjectKey];
    if (!currentContext) {
      return;
    }

    if (Array.isArray(currentContext)) {
      // For arrays, update each item
      currentContext.forEach(item => {
        if (typeof item === 'object' && item !== null) {
          delete item.__isNew;
          if (item.__metadata) {
            item.__metadata.isNewRecord = false;
          }
        }
      });
    } else if (typeof currentContext === 'object' && currentContext !== null) {
      // For single objects
      delete currentContext.__isNew;
      if (currentContext.__metadata) {
        currentContext.__metadata.isNewRecord = false;
      }
    }

    // Emit the updated context so other cells can react if needed
    this.contextDataChange.emit({
      [this.contextObjectKey]: currentContext
    });
  }

  private emitPostSaveRefresh(): void {
    if (this.postSaveRefreshCellIds.length > 0) {
      this.refreshCells.emit(this.postSaveRefreshCellIds);
    }
  }

  /**
   * Apply context field mappings — copies values from other context objects
   * (or system sources like $user / $now) into the target object before saving.
   * Uses the cell-level saveContextFieldMappings list.
   */
  private applyContextFieldMappings(targetObject: any): void {
    this.applyContextFieldMappingsToObject(targetObject, this.saveContextFieldMappings);
  }

  private applyContextFieldMappingsToObject(targetObject: any, mappings: SaveContextFieldMapping[]): void {
    for (const mapping of mappings) {
      if (!mapping.sourceContextVariable || !mapping.sourceField || !mapping.targetField) continue;

      const sourceValue = this.resolveMappingSourceValue(mapping);
      if (sourceValue === undefined) continue;

      if (Array.isArray(targetObject)) {
        targetObject.forEach((item: any) => {
          if (mapping.onlyIfNew && !this.isNewRecord(item)) return;
          item[mapping.targetField] = sourceValue;
        });
      } else {
        if (mapping.onlyIfNew && !this.isNewRecord(targetObject)) continue;
        targetObject[mapping.targetField] = sourceValue;
      }
    }
  }

  /**
   * Resolve the value for a mapping's source. Handles reserved system sources
   * ($user, $now) before falling back to viewManager.contextData lookup.
   */
  private resolveMappingSourceValue(mapping: SaveContextFieldMapping): any {
    if (mapping.sourceContextVariable === '$now') {
      const now = new Date();
      switch (mapping.sourceField) {
        case 'epoch': return now.getTime();
        case 'date':  return now.toISOString().slice(0, 10);
        case 'iso':
        default:      return now.toISOString();
      }
    }

    if (!this.viewManager?.contextData) return undefined;
    const sourceContext = this.viewManager.contextData[mapping.sourceContextVariable];
    if (!sourceContext || typeof sourceContext !== 'object') return undefined;

    const sourceObj = Array.isArray(sourceContext) && sourceContext.length > 0
      ? sourceContext[0]
      : sourceContext;
    return sourceObj[mapping.sourceField];
  }

  private isNewRecord(row: any): boolean {
    if (!row || typeof row !== 'object') return false;
    return row.__isNew === true || row.__metadata?.isNewRecord === true;
  }

  /**
   * Normalize a list context into its flat array of rows. List contexts are bare
   * arrays, so this is a pass-through for them; it also unwraps the legacy
   * { records: [...] } shape, which no producer writes any more. Single-record
   * context objects pass through untouched.
   */
  private unwrapListContext(value: any): any {
    return toContextArray(value) ?? value;
  }

  // ============================================================
  // Chained Save Operations
  // ============================================================

  /**
   * Execute save operations in sequence, with field mapping between operations.
   */
  private executeChainedSaveOperations(): void {
    // Sort operations by order
    const sortedOperations = [...this.saveOperations].sort((a, b) => a.order - b.order);

    if (sortedOperations.length === 0) {
      this.showMessage('No save operations configured', 'error');
      return;
    }

    // Clear previous results
    this.operationResults.clear();
    this.isProcessing = true;

    // Start executing operations sequentially
    this.executeNextOperation(sortedOperations, 0);
  }

  /**
   * Execute a single operation and then proceed to the next one.
   */
  private executeNextOperation(operations: SaveOperation[], index: number): void {
    if (index >= operations.length) {
      // All operations completed successfully
      this.isProcessing = false;
      this.showMessage('All data saved successfully!', 'success');

      // Emit context updates for all modified contexts
      this.emitAllContextUpdates(operations);

      // Trigger refresh on configured cells
      this.emitPostSaveRefresh();
      return;
    }

    const operation = operations[index];

    // Validate operation
    if (!operation.contextVariable) {
      this.handleOperationError(operations, index, 'No context object specified');
      return;
    }

    if (!operation.datasetId) {
      this.handleOperationError(operations, index, 'No dataset specified');
      return;
    }

    // Get context object (unwrap list-shape wrappers so save logic sees a flat row array)
    const rawContextObject = this.viewManager?.contextData?.[operation.contextVariable];
    const contextObject = this.unwrapListContext(rawContextObject);
    if (!contextObject) {
      this.handleOperationError(operations, index, `Context object "${operation.contextVariable}" not found`);
      return;
    }

    // Apply context field mappings (from other context objects) if configured
    if (operation.contextFieldMappings && operation.contextFieldMappings.length > 0) {
      this.applyContextFieldMappingsToObject(contextObject, operation.contextFieldMappings);
    }

    // Apply field mappings from previous operations
    const mappedContextObject = this.applyFieldMappings(operation, contextObject);

    // Handle arrays with auto mode: split new/existing/deleted like saveBulkAutoMode
    const operationSaveMode = operation.saveMode || this.saveMode;
    if (Array.isArray(mappedContextObject) && operationSaveMode === 'auto') {
      this.executeChainedBulkOperation(operations, index, operation, mappedContextObject);
      return;
    }

    // Single object path
    const isNewRecord = this.detectIsNewRecord(mappedContextObject);
    const shouldAdd = operationSaveMode === 'add' || (operationSaveMode === 'auto' && isNewRecord);
    const shouldDelete = operationSaveMode === 'delete';
    const shouldUpsert = operationSaveMode === 'upsert';

    // Prepare data for save
    const dataToSave = this.applyAutoTrim(Array.isArray(mappedContextObject)
      ? mappedContextObject.map(item => this.cleanDataForSave(item))
      : [this.cleanDataForSave(mappedContextObject)]);

    // Execute the operation
    if (shouldDelete) {
      this.dynamicTableService.deleteEntries(operation.datasetId, dataToSave)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (success) => {
            this.operationResults.set(operation.id, {
              operationId: operation.id,
              success: !!success,
              responseData: dataToSave[0]
            });
            this.executeNextOperation(operations, index + 1);
          },
          error: (error) => {
            console.error(`Error deleting ${operation.contextVariable}:`, error);
            this.operationResults.set(operation.id, {
              operationId: operation.id, success: false, responseData: null, error
            });
            this.handleOperationError(operations, index, `Error deleting ${operation.contextVariable}`);
          }
        });
    } else if (shouldUpsert) {
      this.dynamicTableService.upsertEntries(operation.datasetId, dataToSave)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (success) => {
            this.operationResults.set(operation.id, {
              operationId: operation.id,
              success: !!success,
              responseData: dataToSave[0]
            });
            // Upsert may have inserted — strip __isNew so subsequent saves don't re-insert
            this.markContextAsExisting(mappedContextObject);
            this.executeNextOperation(operations, index + 1);
          },
          error: (error) => {
            console.error(`Error upserting ${operation.contextVariable}:`, error);
            this.operationResults.set(operation.id, {
              operationId: operation.id, success: false, responseData: null, error
            });
            this.handleOperationError(operations, index, `Error upserting ${operation.contextVariable}`);
          }
        });
    } else if (shouldAdd) {
      this.dynamicTableService.addEntriesWithResponse(operation.datasetId, dataToSave)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: DynamicTableEntriesResponse) => {
            const responseData = response.rowData && response.rowData.length > 0
              ? response.rowData[0]
              : dataToSave[0];

            this.operationResults.set(operation.id, {
              operationId: operation.id,
              success: true,
              responseData: responseData
            });

            this.updateContextWithResponse(operation.contextVariable, responseData, true);
            this.executeNextOperation(operations, index + 1);
          },
          error: (error) => {
            console.error(`Error creating ${operation.contextVariable}:`, error);
            this.operationResults.set(operation.id, {
              operationId: operation.id, success: false, responseData: null, error
            });
            this.handleOperationError(operations, index, `Error creating ${operation.contextVariable}`);
          }
        });
    } else {
      this.dynamicTableService.updateEntriesWithResponse(operation.datasetId, dataToSave)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: DynamicTableEntriesResponse) => {
            const responseData = response.rowData && response.rowData.length > 0
              ? response.rowData[0]
              : dataToSave[0];

            this.operationResults.set(operation.id, {
              operationId: operation.id,
              success: true,
              responseData: responseData
            });

            this.executeNextOperation(operations, index + 1);
          },
          error: (error) => {
            console.error(`Error updating ${operation.contextVariable}:`, error);
            this.operationResults.set(operation.id, {
              operationId: operation.id, success: false, responseData: null, error
            });
            this.handleOperationError(operations, index, `Error updating ${operation.contextVariable}`);
          }
        });
    }
  }

  /**
   * Handle a chained operation where the context is an array in auto mode.
   * Splits by __isNew, checks for __deleted companion key, runs add/update/delete in parallel,
   * then proceeds to the next operation.
   */
  private executeChainedBulkOperation(operations: SaveOperation[], index: number, operation: SaveOperation, contextArray: any[]): void {
    const newRows: any[] = [];
    const existingRows: any[] = [];

    contextArray.forEach(item => {
      if (typeof item === 'object' && item !== null) {
        if (this.detectIsNewRecord(item)) {
          newRows.push(item);
        } else {
          existingRows.push(item);
        }
      }
    });

    // Check for deleted rows
    const deletedRows: any[] = [];
    const deletedKey = operation.contextVariable + '__deleted';
    if (this.viewManager?.contextData?.[deletedKey]) {
      const deleted = this.viewManager.contextData[deletedKey];
      if (Array.isArray(deleted)) {
        deleted.forEach(item => {
          if (typeof item === 'object' && item !== null) {
            deletedRows.push(this.cleanDataForSave(item));
          }
        });
      }
    }

    const cleanNewRows = this.applyAutoTrim(newRows.map(r => this.cleanDataForSave(r)));
    const cleanExistingRows = this.applyAutoTrim(existingRows.map(r => this.cleanDataForSave(r)));
    const trimmedDeletedRows = this.applyAutoTrim(deletedRows);

    if (cleanNewRows.length === 0 && cleanExistingRows.length === 0 && trimmedDeletedRows.length === 0) {
      // Nothing to do for this operation, proceed to next
      this.operationResults.set(operation.id, {
        operationId: operation.id, success: true, responseData: contextArray
      });
      this.executeNextOperation(operations, index + 1);
      return;
    }

    const bulkOps: Observable<boolean>[] = [];
    if (cleanNewRows.length > 0) {
      bulkOps.push(this.dynamicTableService.addEntries(operation.datasetId, cleanNewRows));
    }
    if (cleanExistingRows.length > 0) {
      bulkOps.push(this.dynamicTableService.updateEntries(operation.datasetId, cleanExistingRows));
    }
    if (trimmedDeletedRows.length > 0) {
      bulkOps.push(this.dynamicTableService.deleteEntries(operation.datasetId, trimmedDeletedRows));
    }

    forkJoin(bulkOps)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (results) => {
          const allSuccess = results.every(r => r);

          if (allSuccess) {
            // Strip __isNew flags
            this.markContextAsExistingForKey(operation.contextVariable, contextArray);

            // Clear deleted rows tracker
            if (this.viewManager?.contextData?.[deletedKey]) {
              this.viewManager.contextData[deletedKey] = [];
            }

            this.operationResults.set(operation.id, {
              operationId: operation.id, success: true, responseData: contextArray
            });
            this.executeNextOperation(operations, index + 1);
          } else {
            this.operationResults.set(operation.id, {
              operationId: operation.id, success: false, responseData: null
            });
            this.handleOperationError(operations, index, `Some operations failed for ${operation.contextVariable}`);
          }
        },
        error: (error) => {
          console.error(`Error during bulk save for ${operation.contextVariable}:`, error);
          this.operationResults.set(operation.id, {
            operationId: operation.id, success: false, responseData: null, error
          });
          this.handleOperationError(operations, index, `Error saving ${operation.contextVariable}`);
        }
      });
  }

  /**
   * Strip __isNew flags from an array context by key name.
   */
  private markContextAsExistingForKey(contextVariable: string, contextArray: any[]): void {
    contextArray.forEach(item => {
      if (typeof item === 'object' && item !== null) {
        delete item.__isNew;
        if (item.__metadata) {
          item.__metadata.isNewRecord = false;
        }
      }
    });
  }

  /**
   * Apply field mappings from previous operation results to the current context object.
   */
  private applyFieldMappings(operation: SaveOperation, contextObject: any): any {
    if (!operation.fieldMappingsFromPrevious || operation.fieldMappingsFromPrevious.length === 0) {
      return contextObject;
    }

    // Resolve mapped values from previous operations
    const mappedValues: { [targetField: string]: any } = {};
    operation.fieldMappingsFromPrevious.forEach(mapping => {
      if (!mapping.sourceOperationId || !mapping.sourceField || !mapping.targetField) {
        return;
      }
      const sourceResult = this.operationResults.get(mapping.sourceOperationId);
      if (!sourceResult || !sourceResult.success || !sourceResult.responseData) {
        console.warn(`Source operation ${mapping.sourceOperationId} result not found or failed`);
        return;
      }
      mappedValues[mapping.targetField] = sourceResult.responseData[mapping.sourceField];
    });

    if (Object.keys(mappedValues).length === 0) {
      return contextObject;
    }

    // For arrays, apply mapped values to each item in the array
    if (Array.isArray(contextObject)) {
      contextObject.forEach(item => {
        if (typeof item === 'object' && item !== null) {
          Object.assign(item, mappedValues);
        }
      });
      return contextObject;
    }

    // For single objects, apply to a copy
    const mappedObject = { ...contextObject, ...mappedValues };

    // Also update the actual context object so subsequent operations see the change
    if (this.viewManager?.contextData?.[operation.contextVariable]) {
      Object.assign(this.viewManager.contextData[operation.contextVariable], mappedValues);
    }

    return mappedObject;
  }

  /**
   * Extract the response data from the API response.
   * For add operations, this should include the newly assigned IDs.
   */
  private extractResponseData(response: any, originalData: any): any {
    // The response structure depends on the API
    // Typically, add-entries returns the created records with IDs
    if (response && typeof response === 'object') {
      // If response is an array, take the first item
      if (Array.isArray(response) && response.length > 0) {
        return response[0];
      }
      // If response has a data property (common pattern)
      if (response.data) {
        if (Array.isArray(response.data) && response.data.length > 0) {
          return response.data[0];
        }
        return response.data;
      }
      // If response has rowData property
      if (response.rowData) {
        if (Array.isArray(response.rowData) && response.rowData.length > 0) {
          return response.rowData[0];
        }
        return response.rowData;
      }
      // Response might be the data itself
      return response;
    }

    // If response is just a success boolean, return the original data
    // (for update operations that don't return the updated record)
    return originalData;
  }

  /**
   * Update the context object with the response data from a successful save.
   */
  private updateContextWithResponse(contextVariable: string, responseData: any, wasAdd: boolean): void {
    if (!this.viewManager?.contextData || !contextVariable) {
      return;
    }

    const currentContext = this.viewManager.contextData[contextVariable];
    if (!currentContext || typeof currentContext !== 'object') {
      return;
    }

    // Merge response data into context (preserving __metadata)
    const metadata = currentContext.__metadata;
    Object.assign(currentContext, responseData);

    // Restore metadata but update flags after successful add
    if (metadata) {
      currentContext.__metadata = metadata;
      if (wasAdd) {
        currentContext.__metadata.isNewRecord = false;
        currentContext.__metadata.isEmptyContext = false;
      }
    }

    // Remove __isNew flag after successful add
    if (wasAdd) {
      delete currentContext.__isNew;
    }
  }

  /**
   * Handle an error during chained operations.
   */
  private handleOperationError(operations: SaveOperation[], failedIndex: number, errorMessage: string): void {
    this.isProcessing = false;

    const operation = operations[failedIndex];
    const completedCount = failedIndex;
    const totalCount = operations.length;

    if (completedCount > 0) {
      this.showMessage(
        `${errorMessage}. ${completedCount} of ${totalCount} operations completed before failure.`,
        'error'
      );
    } else {
      this.showMessage(errorMessage, 'error');
    }
  }

  /**
   * Emit context updates for all contexts that were modified during chained saves.
   */
  private emitAllContextUpdates(operations: SaveOperation[]): void {
    const contextUpdates: { [contextKey: string]: any } = {};

    operations.forEach(operation => {
      if (operation.contextVariable && this.viewManager?.contextData) {
        const contextData = this.viewManager.contextData[operation.contextVariable];
        if (contextData) {
          contextUpdates[operation.contextVariable] = contextData;
        }
      }
    });

    if (Object.keys(contextUpdates).length > 0) {
      this.contextDataChange.emit(contextUpdates);
    }
  }

  private showMessage(message: string, type: 'success' | 'error'): void {
    // Simple alert for now - could be replaced with a toast notification service
    if (type === 'success') {
      alert(message);
    } else {
      alert(`Error: ${message}`);
    }
  }

  getButtonVariant(): string {
    switch (this.buttonStyle) {
      case 'flat':
        return 'flat';
      case 'stroked':
        return 'stroked';
      case 'raised':
      default:
        return 'raised';
    }
  }
}
